import { fnv1a, mulberry32 } from './prng';
import { STAT_FIELDS, emptyStats, type StatField } from './lifetimeStats';

/**
 * Quests (spec: docs/superpowers/specs/2026-09-08-quests-and-potions-design.md
 * §2). Pure: the clock and the player's salt are passed in. Progress is
 * never stored — each period keeps a BASELINE snapshot of the lifetime
 * counters taken when it rolled, and progress is `now − baseline`.
 */

export type QuestPeriod = 'daily' | 'weekly';
export const QUEST_PERIODS: readonly QuestPeriod[] = ['daily', 'weekly'];

/** What a quest counts: a lifetime counter, or a value the save already keeps. */
export type QuestMetric = StatField | 'waves' | 'rankUps' | 'achievements';
export const QUEST_METRICS: readonly QuestMetric[] = [
  ...STAT_FIELDS,
  'waves',
  'rankUps',
  'achievements',
];

export interface QuestDef {
  metric: QuestMetric;
  goal: number;
  /** The on-screen line. */
  name: string;
}

export const QUESTS_PER_SET = 3;

export const DAILY_SETS: readonly (readonly QuestDef[])[] = [
  [
    { metric: 'kills', goal: 100, name: 'Beat 100 enemies' },
    { metric: 'clears', goal: 3, name: 'Clear 3 levels' },
    { metric: 'obtains', goal: 1, name: 'Obtain 1 tower or item' },
  ],
  [
    { metric: 'arenaWaves', goal: 1, name: 'Beat a wave in the Arena' },
    { metric: 'potionsBought', goal: 1, name: 'Buy 1 potion' },
    { metric: 'bagsOpened', goal: 1, name: 'Open 1 bag' },
  ],
  [
    { metric: 'rankUps', goal: 1, name: 'Rank up once' },
    { metric: 'bossKills', goal: 1, name: 'Beat 1 boss' },
    { metric: 'bagsOpened', goal: 2, name: 'Open 2 bags' },
  ],
];

export const WEEKLY_SETS: readonly (readonly QuestDef[])[] = [
  [
    { metric: 'kills', goal: 500, name: 'Beat 500 enemies' },
    { metric: 'clears', goal: 5, name: 'Complete 5 levels' },
    { metric: 'rankUps', goal: 10, name: 'Rank up 10 times' },
  ],
  [
    { metric: 'waves', goal: 100, name: 'Complete 100 waves' },
    { metric: 'achievements', goal: 2, name: 'Earn 2 achievements' },
    { metric: 'sells', goal: 5, name: 'Sell 5 towers' },
  ],
  [
    { metric: 'clears', goal: 5, name: 'Complete 5 levels' },
    { metric: 'coinsEarned', goal: 750, name: 'Earn 750 coins' },
    { metric: 'bossKills', goal: 3, name: 'Beat 3 bosses' },
  ],
];

export const QUEST_SETS: Record<QuestPeriod, readonly (readonly QuestDef[])[]> = {
  daily: DAILY_SETS,
  weekly: WEEKLY_SETS,
};

export const QUEST_REWARD: Record<QuestPeriod, { coins: number; exp: number }> = {
  daily: { coins: 100, exp: 500 },
  weekly: { coins: 300, exp: 750 },
};

export const DAY_MS = 86_400_000;
export const WEEK_MS = 7 * DAY_MS;
/** Monday 1970-01-05 00:00 UTC: weekly periods roll on Monday for everyone. */
export const WEEK_EPOCH = Date.UTC(1970, 0, 5);
const PERIOD_MS: Record<QuestPeriod, number> = { daily: DAY_MS, weekly: WEEK_MS };
const PERIOD_EPOCH: Record<QuestPeriod, number> = { daily: 0, weekly: WEEK_EPOCH };

/** Which UTC day / Monday-week `now` (epoch ms) falls in. */
export function periodIndex(period: QuestPeriod, now: number): number {
  return Math.floor((now - PERIOD_EPOCH[period]) / PERIOD_MS[period]);
}

/** Epoch ms at which the current period's quests are replaced. */
export function periodEndsAt(period: QuestPeriod, now: number): number {
  return PERIOD_EPOCH[period] + (periodIndex(period, now) + 1) * PERIOD_MS[period];
}

export type QuestSnapshot = Record<QuestMetric, number>;

export function emptySnapshot(): QuestSnapshot {
  return { ...emptyStats(), waves: 0, rankUps: 0, achievements: 0 };
}

export interface QuestPeriodState {
  index: number;
  set: number;
  baseline: QuestSnapshot;
  claimed: boolean[];
}

/**
 * The Quester streak (spec 2026-10-29): consecutive daily periods on which all
 * three daily quests were CLAIMED. `day` is the last such daily period index.
 * Kept at the top level of `hambi.quests`, never inside a period — a period is
 * dropped when it rolls over, and the streak must outlive it.
 */
export interface DailyStreak {
  day: number;
  count: number;
}

/** Full days in a row that earn Quester. */
export const QUESTER_STREAK = 5;

export interface QuestState {
  daily: QuestPeriodState | null;
  weekly: QuestPeriodState | null;
  /** Absent until the first full day; optional so old saves parse unchanged. */
  streak?: DailyStreak;
}

export const QUESTS_KEY = 'hambi.quests';

function parsePeriod(v: unknown): QuestPeriodState | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  if (typeof r.index !== 'number' || !Number.isInteger(r.index)) return null;
  if (
    typeof r.set !== 'number' ||
    !Number.isInteger(r.set) ||
    r.set < 0 ||
    r.set >= DAILY_SETS.length
  ) {
    return null;
  }
  if (!r.baseline || typeof r.baseline !== 'object') return null;
  const b = r.baseline as Record<string, unknown>;
  const baseline = emptySnapshot();
  for (const m of QUEST_METRICS) {
    const n = b[m];
    if (typeof n === 'number' && Number.isFinite(n) && n >= 0) baseline[m] = Math.floor(n);
  }
  const c = Array.isArray(r.claimed) ? r.claimed : [];
  const claimed = Array.from({ length: QUESTS_PER_SET }, (_, i) => c[i] === true);
  return { index: r.index, set: r.set, baseline, claimed };
}

function parseStreak(v: unknown): DailyStreak | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as Record<string, unknown>;
  if (typeof r.day !== 'number' || !Number.isInteger(r.day)) return undefined;
  if (typeof r.count !== 'number' || !Number.isInteger(r.count) || r.count < 1) return undefined;
  return { day: r.day, count: r.count };
}

/** Parse `hambi.quests`; each half hardens on its own. Never throws. */
export function parseQuests(raw: string | null): QuestState {
  const none: QuestState = { daily: null, weekly: null };
  if (!raw) return none;
  try {
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== 'object') return none;
    const r = p as Record<string, unknown>;
    const out: QuestState = { daily: parsePeriod(r.daily), weekly: parsePeriod(r.weekly) };
    const streak = parseStreak(r.streak);
    if (streak) out.streak = streak;
    return out;
  } catch {
    return none;
  }
}

/**
 * The streak after daily period `day` is fully claimed: +1 when the previous
 * full day was yesterday, back to 1 after a gap, unchanged on a repeat.
 */
export function nextDailyStreak(prev: DailyStreak | undefined, day: number): DailyStreak {
  if (prev && prev.day === day) return prev;
  return { day, count: prev && prev.day === day - 1 ? prev.count + 1 : 1 };
}

/** Which of the three sets this player gets for a period — "each player gets a different set". */
export function pickSet(period: QuestPeriod, index: number, salt: string): number {
  const sets = QUEST_SETS[period].length;
  const u = mulberry32(fnv1a(`${period}:${index}:${salt}`))();
  return Math.min(sets - 1, Math.floor(u * sets));
}

export function rollPeriod(
  period: QuestPeriod,
  index: number,
  salt: string,
  snapshot: QuestSnapshot,
): QuestPeriodState {
  return {
    index,
    set: pickSet(period, index, salt),
    baseline: { ...snapshot },
    claimed: Array.from({ length: QUESTS_PER_SET }, () => false),
  };
}

/**
 * The stored period when it is still the current one, else a fresh roll
 * (the old one — and any unclaimed reward — is dropped). The caller persists.
 */
export function currentPeriod(
  state: QuestPeriodState | null,
  period: QuestPeriod,
  now: number,
  salt: string,
  snapshot: QuestSnapshot,
): QuestPeriodState {
  const index = periodIndex(period, now);
  if (state && state.index === index) return state;
  return rollPeriod(period, index, salt, snapshot);
}

export function questDefs(period: QuestPeriod, set: number): readonly QuestDef[] {
  return QUEST_SETS[period][set] ?? QUEST_SETS[period][0];
}

/** Progress toward `def`: current minus baseline, clamped to [0, goal]. */
export function questProgress(
  def: QuestDef,
  baseline: QuestSnapshot,
  snapshot: QuestSnapshot,
): number {
  return Math.min(def.goal, Math.max(0, snapshot[def.metric] - baseline[def.metric]));
}

export interface QuestRow {
  def: QuestDef;
  progress: number;
  complete: boolean;
  claimed: boolean;
}

export function questRows(
  ps: QuestPeriodState,
  period: QuestPeriod,
  snapshot: QuestSnapshot,
): QuestRow[] {
  return questDefs(period, ps.set).map((def, i) => {
    const progress = questProgress(def, ps.baseline, snapshot);
    return { def, progress, complete: progress >= def.goal, claimed: ps.claimed[i] === true };
  });
}

/** The period with quest `i` claimed, or null when it is not complete or already claimed. */
export function claimQuest(
  ps: QuestPeriodState,
  period: QuestPeriod,
  i: number,
  snapshot: QuestSnapshot,
): QuestPeriodState | null {
  const row = questRows(ps, period, snapshot)[i];
  if (!row || !row.complete || row.claimed) return null;
  const claimed = ps.claimed.slice();
  claimed[i] = true;
  return { ...ps, claimed };
}

/** `hh:mm:ss`, with `Nd ` in front once a day or more remains; never negative. */
export function formatQuestCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}
